import type {
  Cart,
  CartItem,
  CouponCode,
  MoneyInfo,
  Order,
  Ticket,
} from 'services/api/restful/generated/nofomoRestApi.schemas';
import {
  formatEventDate,
  mapPageEventToDatalayer,
} from 'services/datalayer/utils';

const calculateDiscount = (
  originalPrice: MoneyInfo | null | undefined,
  discountedPrice: MoneyInfo | null | undefined,
) => {
  if (!originalPrice || !discountedPrice) return 0;
  if (originalPrice.amount === discountedPrice.amount) return 0;

  return originalPrice.amount - discountedPrice.amount;
};

export const mapTicketToDataLayer = (
  item: Pick<Ticket, 'id' | 'name'>,
  event: ReturnType<typeof mapPageEventToDatalayer>,
  {
    originalPrice,
    discountedPrice,
  }: {
    originalPrice: MoneyInfo | undefined;
    discountedPrice?: MoneyInfo | undefined;
  },
) => ({
  itemId: item.id,
  itemName: item.name?.['en'] ?? '',
  itemCategory: event.hostingEventName ?? '',
  itemCategory2: formatEventDate(event.eventDate),
  itemBrand: '',
  itemListName: 'Event detail',
  index: 0,
  discount: calculateDiscount(originalPrice, discountedPrice),
  price: discountedPrice?.amount ?? 0,
  quantity: 1,
});

const mapCartItemToDataLayer = (
  item: CartItem,
  idx: number = 0,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  itemId: item.catalogueItemId,
  itemName: item.name?.['en'] ?? '',
  itemCategory: event.hostingEventName ?? '',
  itemCategory2: formatEventDate(event.eventDate),
  itemBrand: '',
  itemListName: 'Event detail',
  discount: calculateDiscount(
    item.originalItemPrice ?? undefined,
    item.discountedItemPrice ?? undefined,
  ),
  index: idx,
  price: item.discountedItemPrice?.amount ?? 0,
  quantity: item.quantity,
});

export const mapOrderToDatalayer = (
  order: Order,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  currency: 'SAR' as const,
  value: order.totalPrice?.amount ?? 0,
  items:
    order.items?.map((item, idx) => mapCartItemToDataLayer(item, idx, event)) ??
    [],
  transactionId: order.id,
  coupon: findCouponCode(order.couponCodes),
  taxAmount: order.vatAmount?.amount ?? 0,
  // Always 0 for now
  shippingAmount: 0,
  paymentType:
    (order.totalPrice?.amount ?? 0) === 0
      ? 'FreeOfCharge'
      : order.paymentMethod,
  shippingTier: 'Online',
});

export const mapCartToDatalayer = (
  cart: Cart,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  currency: 'SAR' as const,
  value: cart.totalPrice?.amount ?? 0,
  items:
    cart.items?.map((item, idx) => mapCartItemToDataLayer(item, idx, event)) ??
    [],
});

const findCoupon = (coupons: Array<CouponCode>) =>
  coupons.find((coupon) => coupon.couponType === 'RegularCoupon');

export const findValidCouponCode = (coupons: Array<CouponCode>) => {
  const coupon = findCoupon(coupons);
  return (coupon?.discountValue?.amount ?? 0 > 0) ? coupon?.code : undefined;
};

const findCouponCode = (coupons: Array<CouponCode>) =>
  findCoupon(coupons)?.code;
